import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Loader } from '../components/ui/Loader';
import { PurchaseWizard } from '../components/temporary-numbers/PurchaseWizard';
import { useGetServersQuery } from '../store/api/temporaryNumbersApi';
import { useGetDisabledServersQuery } from '../store/api/proxiesApi';

const TEMP_SERVER_TO_KEY: Record<number, string> = {
  1: "smspool",
  4: "durianRcs",
  5: "textverified_temp",
  6: "pvadeals6",
};

export function PurchaseTemporaryNumberPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const serverIdParam = searchParams.get('serverId');

  const { data: serversData, isLoading: isLoadingServers } = useGetServersQuery();
  const { data: disabledData } = useGetDisabledServersQuery();
  const disabledServers: string[] = useMemo(
    () => disabledData?.data?.disabledServers || [],
    [disabledData],
  );
  const selectedServer =
    serverIdParam && serversData?.data.servers
      ? serversData.data.servers.find((s) => s.serverId === Number(serverIdParam)) || null
      : null;
  const isServerDisabled = selectedServer ? disabledServers.includes(TEMP_SERVER_TO_KEY[selectedServer.serverId]) : false;

  const handlePurchaseComplete = (temporaryNumberId: string) => {
    navigate(`/temporary-numbers/${temporaryNumberId}`);
  };

  const handleCancel = () => {
    navigate('/temporary-numbers');
  };

  if (isLoadingServers) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedServer && serverIdParam) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('errors.notFound')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {t('temporaryNumbers.purchase.serverNotFound')}
              </p>
              <button
                onClick={handleCancel}
                className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('common.back')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('temporaryNumbers.purchase.title')}
            </h1>
          </div>

          {/* Purchase Wizard */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 animate-scale-in">
            <PurchaseWizard
              selectedServer={selectedServer}
              disabled={isServerDisabled}
              onPurchaseComplete={handlePurchaseComplete}
              onCancel={handleCancel}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

